import { Stack, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { IconTile, type Tone } from './IconTile';

/**
 * The top of a page: a tile, the title, a line of context, and the actions.
 *
 * Every screen used to open with a bare `<Typography variant="h1">`, which
 * meant that thirteen pages opened identically and the only way to know where
 * you were was to read the word. The tile repeats the sidebar's icon for the
 * section, so arriving at a page confirms the click rather than describing it.
 *
 * `subtitle` is for what the page is counting, not for instructions — "148
 * customers", "12 of 40 stops" — and is left out when there is nothing true
 * to put there.
 */
export function PageHeader({
  icon,
  tone = 'accent',
  title,
  subtitle,
  actions,
}: {
  icon?: SvgIconComponent;
  tone?: Tone;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 2 }}
    >
      {icon ? <IconTile icon={icon} tone={tone} size={44} /> : null}

      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="h1" noWrap>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Stack>

      {actions ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
}
